import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
    PiArrowDownRightBold,
    PiArrowUpRightBold,
    PiArrowRightBold,
    PiChartBarBold,
    PiCheckCircleBold,
    PiClockBold,
    PiCurrencyDollarBold,
    PiGearBold,
    PiReceiptBold,
    PiShieldCheckBold,
    PiTrendUpBold,
    PiUsersBold,
    PiWarningBold,
    PiCalendarCheckBold,
    PiListChecksBold,
    PiChartLineUpBold,
} from 'react-icons/pi';
import { LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import type { CommissionExecutiveDashboard } from './types';
import {
    buildDecisionAlerts,
    DecisionAlertsPanel,
    fmtMonthShort,
    MonthlyComboChart,
    RoleStackedChart,
    Sparkline,
    StatusDonutChart,
    TopEarnersChart,
} from './CommissionExecutiveCharts';
import BillingReportSection from './BillingReportSection';
import LegacyMarkPaidPanel from './LegacyMarkPaidPanel';
import { isSuperAdmin } from '@v2/lib/auth';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

const brlFull = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 }).format(v ?? 0);

// ── KPI hero card ─────────────────────────────────────────────────────────────
function HeroKpi({
    label, value, sub, icon: Icon, color = 'emerald', trend, trendLabel, sparkline,
}: {
    label: string; value: string; sub?: string; icon: React.ElementType;
    color?: 'emerald' | 'amber' | 'orange' | 'blue' | 'violet';
    trend?: 'up' | 'down' | 'neutral'; trendLabel?: string; sparkline?: number[];
}) {
    const colors = {
        emerald: 'border-emerald-100 bg-gradient-to-br from-emerald-50 to-white dark:border-emerald-900/40 dark:from-emerald-950/30 dark:to-zinc-900',
        amber:   'border-amber-100 bg-gradient-to-br from-amber-50 to-white dark:border-amber-900/40 dark:from-amber-950/30 dark:to-zinc-900',
        orange:  'border-orange-100 bg-gradient-to-br from-orange-50 to-white dark:border-orange-900/40 dark:from-orange-950/30 dark:to-zinc-900',
        blue:    'border-blue-100 bg-gradient-to-br from-blue-50 to-white dark:border-blue-900/40 dark:from-blue-950/30 dark:to-zinc-900',
        violet:  'border-violet-100 bg-gradient-to-br from-violet-50 to-white dark:border-violet-900/40 dark:from-violet-950/30 dark:to-zinc-900',
    };
    const iconColors = {
        emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
        amber:   'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
        orange:  'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400',
        blue:    'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
        violet:  'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400',
    };
    const valueColors = {
        emerald: 'text-emerald-900 dark:text-emerald-100',
        amber:   'text-amber-900 dark:text-amber-100',
        orange:  'text-orange-900 dark:text-orange-100',
        blue:    'text-blue-900 dark:text-blue-100',
        violet:  'text-violet-900 dark:text-violet-100',
    };
    return (
        <div className={cn('relative overflow-hidden rounded-2xl border p-5', colors[color])}>
            <div className="flex items-start justify-between gap-2 mb-3">
                <div className={cn('flex h-9 w-9 items-center justify-center rounded-xl', iconColors[color])}>
                    <Icon className="size-5" />
                </div>
                {sparkline && sparkline.length > 1 && <Sparkline values={sparkline} />}
            </div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500 mb-1">{label}</p>
            <p className={cn('text-2xl font-black', valueColors[color])}>{value}</p>
            {sub && <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">{sub}</p>}
            {trendLabel && (
                <p className={cn('mt-2 flex items-center gap-1 text-xs font-semibold',
                    trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-rose-600' : 'text-gray-500')}>
                    {trend === 'up' && <PiArrowUpRightBold className="size-3.5" />}
                    {trend === 'down' && <PiArrowDownRightBold className="size-3.5" />}
                    {trendLabel}
                </p>
            )}
        </div>
    );
}

// ── Card container ─────────────────────────────────────────────────────────────
function DashCard({ title, subtitle, action, children, className }: {
    title: string; subtitle?: string; action?: React.ReactNode;
    children: React.ReactNode; className?: string;
}) {
    return (
        <section className={cn('flex flex-col rounded-2xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900', className)}>
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-zinc-800">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{title}</h3>
                    {subtitle && <p className="mt-0.5 text-xs text-gray-500">{subtitle}</p>}
                </div>
                {action}
            </div>
            <div className="flex-1 px-5 py-4">{children}</div>
        </section>
    );
}

// ── Pending action banner ──────────────────────────────────────────────────────
function PendingBanner({ count, amount, label, linkTo, linkLabel, color }: {
    count: number; amount?: number; label: string;
    linkTo: string; linkLabel: string;
    color: 'amber' | 'red' | 'blue';
}) {
    const colors = {
        amber: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-700/40 dark:bg-amber-950/30 dark:text-amber-100',
        red:   'border-red-200 bg-red-50 text-red-900 dark:border-red-700/40 dark:bg-red-950/30 dark:text-red-100',
        blue:  'border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-700/40 dark:bg-blue-950/30 dark:text-blue-100',
    };
    const btnColors = {
        amber: 'bg-amber-600 hover:bg-amber-700 text-white',
        red:   'bg-red-600 hover:bg-red-700 text-white',
        blue:  'bg-blue-600 hover:bg-blue-700 text-white',
    };
    return (
        <div className={cn('flex items-center justify-between gap-4 rounded-xl border px-4 py-3', colors[color])}>
            <div className="flex items-center gap-3">
                <PiWarningBold className="size-5 shrink-0" />
                <div>
                    <p className="text-sm font-semibold">{count} {label}</p>
                    {amount != null && amount > 0 && (
                        <p className="text-xs opacity-80">{brl(amount)}</p>
                    )}
                </div>
            </div>
            <Link to={linkTo} className={cn('shrink-0 rounded-lg px-3 py-1.5 text-xs font-bold transition flex items-center gap-1', btnColors[color])}>
                {linkLabel} <PiArrowRightBold className="size-3" />
            </Link>
        </div>
    );
}

// ── Navigation module cards ────────────────────────────────────────────────────
function NavCard({ to, icon: Icon, label, desc }: { to: string; icon: React.ElementType; label: string; desc: string }) {
    return (
        <Link
            to={to}
            className="flex items-start gap-3 rounded-xl border border-gray-200 bg-white p-4 transition hover:border-orange-300 hover:bg-orange-50/50 dark:border-zinc-700 dark:bg-zinc-900 dark:hover:border-orange-500/40 dark:hover:bg-orange-500/10"
        >
            <div className="rounded-lg bg-orange-100 p-2 text-orange-600 dark:bg-orange-500/20 dark:text-orange-400">
                <Icon className="size-4" />
            </div>
            <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">{label}</p>
                <p className="mt-0.5 text-xs text-gray-500">{desc}</p>
            </div>
        </Link>
    );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function CommissionDashboard() {
    const [months, setMonths] = useState(12);

    const { data, isLoading, isError } = useQuery<{ data: CommissionExecutiveDashboard }>({
        queryKey: ['commissioning', 'executive-dashboard', months],
        queryFn: () => api.get('/commissioning/executive-dashboard', { params: { months } }).then((r) => r.data),
    });

    const dash = data?.data;
    const kpis = dash?.kpis;

    const momTrend = useMemo(() => {
        if (kpis?.mom_paid_pct == null) return undefined;
        if (kpis.mom_paid_pct > 0) return 'up' as const;
        if (kpis.mom_paid_pct < 0) return 'down' as const;
        return 'neutral' as const;
    }, [kpis?.mom_paid_pct]);

    const alerts = useMemo(() => (dash ? buildDecisionAlerts(dash) : []), [dash]);
    const paidSparkline = dash?.monthly.paid.slice(-6) ?? [];

    const eficiencia = kpis && kpis.accrued_mtd > 0
        ? Math.round((kpis.paid_mtd / kpis.accrued_mtd) * 100)
        : 0;

    return (
        <div className="space-y-5">
            <PageHeader
                title="Comissões · Visão Executiva"
                subtitle="Indicadores e controle do programa de comissionamento"
            >
                <select
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                    value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                >
                    <option value={6}>Últimos 6 meses</option>
                    <option value={12}>Últimos 12 meses</option>
                    <option value={18}>Últimos 18 meses</option>
                </select>
            </PageHeader>

            {isError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    Não foi possível carregar o dashboard. Verifique permissão de super_admin.
                </div>
            )}

            {isLoading && <LoadingState label="Carregando indicadores…" />}

            {dash && kpis && (
                <>
                    {/* ── Hero KPIs ── */}
                    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                        <HeroKpi
                            icon={PiCurrencyDollarBold}
                            label="Pago no ano (YTD)"
                            value={brl(kpis.paid_ytd)}
                            color="emerald"
                            sparkline={paidSparkline}
                            sub={`Mês anterior: ${brl(kpis.paid_prev_month)}`}
                        />
                        <HeroKpi
                            icon={PiTrendUpBold}
                            label="Gerado este mês"
                            value={brl(kpis.accrued_mtd)}
                            color="orange"
                            trend={momTrend}
                            trendLabel={kpis.mom_paid_pct != null ? `${kpis.mom_paid_pct > 0 ? '+' : ''}${kpis.mom_paid_pct}% vs mês ant.` : undefined}
                            sub={`Pago no mês: ${brl(kpis.paid_mtd)}`}
                        />
                        <HeroKpi
                            icon={PiClockBold}
                            label="Pipeline a pagar"
                            value={brl(kpis.pipeline_amount)}
                            color="amber"
                            sub="Registros em aberto"
                        />
                        <HeroKpi
                            icon={PiReceiptBold}
                            label="Aguardando NF"
                            value={String(kpis.awaiting_nf_count)}
                            color="violet"
                            sub={brl(kpis.awaiting_nf_amount)}
                        />
                    </div>

                    {/* ── Pending actions ── */}
                    {(kpis.awaiting_nf_count > 0 || kpis.pipeline_amount > 0) && (
                        <div className="grid gap-3 sm:grid-cols-2">
                            {kpis.awaiting_nf_count > 0 && (
                                <PendingBanner
                                    count={kpis.awaiting_nf_count}
                                    amount={kpis.awaiting_nf_amount}
                                    label="vendedores aguardando envio de NF"
                                    linkTo="records?status=awaiting_nf"
                                    linkLabel="Cobrar NFs"
                                    color="amber"
                                />
                            )}
                            <PendingBanner
                                count={1}
                                amount={kpis.pipeline_amount}
                                label="em pipeline — liberar pagamentos"
                                linkTo="payment-approvals"
                                linkLabel="Aprovar pagamentos"
                                color="blue"
                            />
                        </div>
                    )}

                    <DecisionAlertsPanel alerts={alerts} />

                    {/* ── Gráfico principal + Top earners ── */}
                    <div className="grid gap-5 lg:grid-cols-5">
                        <DashCard
                            className="lg:col-span-3"
                            title="Evolução mensal"
                            subtitle="Comissão paga · Gerada por mês (data do deal)"
                            action={
                                <Link to="cashflow" className="text-xs font-medium text-orange-600 hover:underline">
                                    Fluxo detalhado
                                </Link>
                            }
                        >
                            {dash.monthly.labels.length > 0 ? (
                                <MonthlyComboChart
                                    labels={dash.monthly.labels}
                                    paid={dash.monthly.paid}
                                    accrued={dash.monthly.accrued}
                                    cumulative={dash.monthly.cumulative_paid}
                                />
                            ) : (
                                <p className="py-12 text-center text-sm text-gray-400">Sem dados no período.</p>
                            )}
                        </DashCard>

                        <DashCard
                            className="lg:col-span-2"
                            title="Top comissionados"
                            subtitle="Maiores valores do período"
                            action={
                                <Link to="org-chart" className="text-xs font-medium text-orange-600 hover:underline">
                                    Ranking global
                                </Link>
                            }
                        >
                            {dash.top_earners.length > 0 ? (
                                <div className="space-y-2">
                                    {dash.top_earners.slice(0, 8).map((e, i) => (
                                        <Link
                                            key={e.user_id}
                                            to={`sellers/${e.user_id}`}
                                            className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition hover:bg-orange-50 dark:hover:bg-orange-950/20"
                                        >
                                            <span className="w-5 shrink-0 text-center text-xs font-bold text-gray-400">
                                                {i + 1}
                                            </span>
                                            {e.avatar_url ? (
                                                <img src={e.avatar_url} alt="" className="size-7 rounded-full object-cover" />
                                            ) : (
                                                <span className="flex size-7 items-center justify-center rounded-full bg-orange-100 text-[10px] font-bold text-orange-700">
                                                    {e.name.trim().split(/\s+/).slice(0, 2).map((p: string) => p[0]).join('')}
                                                </span>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-xs font-semibold text-gray-900 dark:text-zinc-100">{e.name.split(' ')[0]}</p>
                                                <p className="text-[10px] text-gray-400">{e.role_label}</p>
                                            </div>
                                            <div className="shrink-0 text-right">
                                                <p className="text-xs font-bold text-emerald-700">{brl(e.total_paid)}</p>
                                                {e.total_pending > 0 && (
                                                    <p className="text-[10px] text-amber-600">+{brl(e.total_pending)}</p>
                                                )}
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <p className="py-8 text-center text-sm text-gray-400">Sem pagamentos no período.</p>
                            )}
                        </DashCard>
                    </div>

                    {/* ── Tabela mensal + Funil de status ── */}
                    <div className="grid gap-5 lg:grid-cols-3">
                        <DashCard className="lg:col-span-2" title="Detalhamento mensal" subtitle="Por data do deal · valores de comissão">
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-left dark:border-zinc-800">
                                            <th className="pb-2.5 font-semibold text-gray-600">Mês</th>
                                            <th className="pb-2.5 text-right font-semibold text-orange-600">Gerado</th>
                                            <th className="pb-2.5 text-right font-semibold text-emerald-700">Pago</th>
                                            <th className="pb-2.5 text-right font-semibold text-gray-500">Deals</th>
                                            <th className="pb-2.5 text-right font-semibold text-blue-600">Vol. Vendas</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...dash.monthly.labels].reverse().map((ym, revIdx) => {
                                            const i = dash.monthly.labels.length - 1 - revIdx;
                                            const hasActivity = dash.monthly.accrued[i] > 0 || dash.monthly.paid[i] > 0;
                                            return (
                                                <tr key={ym} className={cn('border-b border-gray-50 dark:border-zinc-800/50', !hasActivity && 'opacity-40')}>
                                                    <td className="py-2 capitalize font-semibold text-gray-700 dark:text-zinc-300">{fmtMonthShort(ym)}</td>
                                                    <td className="py-2 text-right tabular-nums text-orange-600 dark:text-orange-400">{brl(dash.monthly.accrued[i])}</td>
                                                    <td className="py-2 text-right tabular-nums font-semibold text-emerald-700 dark:text-emerald-400">{brl(dash.monthly.paid[i])}</td>
                                                    <td className="py-2 text-right tabular-nums text-gray-500">{dash.monthly.deals[i]}</td>
                                                    <td className="py-2 text-right tabular-nums text-blue-600 dark:text-blue-400">{brl(dash.monthly.deal_volume[i])}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </DashCard>

                        <div className="space-y-5">
                            <DashCard title="Funil de comissões" subtitle="Por status">
                                <StatusDonutChart items={dash.by_status} />
                            </DashCard>

                            <DashCard title="Ações rápidas" subtitle="Atalhos operacionais">
                                <div className="space-y-2">
                                    <Link to="payment-approvals" className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm font-semibold text-blue-800 transition hover:bg-blue-100 dark:border-blue-800/40 dark:bg-blue-950/30 dark:text-blue-200">
                                        <PiCheckCircleBold className="size-4 shrink-0" />
                                        Liberar pagamentos
                                    </Link>
                                    <Link to="records?status=awaiting_nf" className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 dark:border-amber-800/40 dark:bg-amber-950/30 dark:text-amber-200">
                                        <PiReceiptBold className="size-4 shrink-0" />
                                        Cobrar NFs pendentes
                                    </Link>
                                    <Link to="org-chart" className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                                        <PiChartBarBold className="size-4 shrink-0" />
                                        Relatório por equipe
                                    </Link>
                                    <Link to="goals" className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                                        <PiCalendarCheckBold className="size-4 shrink-0" />
                                        Gerenciar metas
                                    </Link>
                                </div>
                            </DashCard>
                        </div>
                    </div>

                    {/* ── Por nível hierárquico ── */}
                    {dash.by_role.length > 0 && (
                        <DashCard title="Por nível hierárquico" subtitle="Pago vs a pagar — todos os vendedores">
                            <RoleStackedChart items={dash.by_role} />
                        </DashCard>
                    )}
                </>
            )}

            {/* ── Faturamento Global ── */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-zinc-700 dark:bg-zinc-900">
                <BillingReportSection />
            </div>

            {isSuperAdmin() && <LegacyMarkPaidPanel />}

            {/* ── Módulos ── */}
            <div>
                <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">Módulos</h2>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                    <NavCard to="records"           icon={PiListChecksBold}    label="Registros"         desc="Filtrar e gerenciar comissões" />
                    <NavCard to="org-chart"         icon={PiChartBarBold}      label="Relatório Global"  desc="Hierarquia por período" />
                    <NavCard to="teams"             icon={PiUsersBold}         label="Times de Vendas"   desc="Gerentes e vendedores" />
                    <NavCard to="goals"             icon={PiCalendarCheckBold} label="Metas (Bucket)"    desc="Super Gatilho Q3" />
                    <NavCard to="structures"        icon={PiGearBold}          label="Estruturas"        desc="Percentuais por nível" />
                    <NavCard to="cashflow"          icon={PiChartLineUpBold}   label="Fluxo de Comissão" desc="Projeção de comissões" />
                    <NavCard to="audit"             icon={PiShieldCheckBold}   label="Auditoria"         desc="Histórico de alterações" />
                    <NavCard to="payment-rules"     icon={PiReceiptBold}       label="Regras de Pag."    desc="Prazos e condições" />
                </div>
            </div>
        </div>
    );
}
